import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../shared";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, useFinancialYears, downloadReportCsv, formatCurrency } from "./reportShared";

/**
 * Phase 8 - Fund Position Report (items 31/32). Every Fund's own Opening/
 * Additions/Utilisation/Closing, derived from that Fund's dedicated 1:1
 * ledger - never a manually maintained dashboard counter.
 */
export default function FundPositionPage() {
  const navigate = useNavigate();
  const financialYears = useFinancialYears();
  const [financialYearId, setFinancialYearId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = { financialYearId, fromDate, toDate };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/fund-position", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load fund position"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [financialYearId, fromDate, toDate]);

  const rows = data?.rows || [];
  const openLedger = (ledgerId) => navigate(`${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${ledgerId}&fromDate=${fromDate || data?.fromDate || ""}&toDate=${toDate || data?.toDate || ""}`);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Fund Position</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Reserve, Sinking, Repair and other earmarked Funds - opening, additions, utilisation and closing.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate}
          onExport={() => downloadReportCsv("/society/accounting/reports/fund-position", params, `fund-position-${data?.fromDate || ""}-to-${data?.toDate || ""}.csv`)}
          onPrint={() => window.print()}
        />

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : rows.length === 0 ? (
          <EmptyState>No Funds set up (or none with a linked ledger) yet.</EmptyState>
        ) : (
          <SectionCard subtitle={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
            <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fund</TableHead><TableHead className="text-right">Opening</TableHead>
                    <TableHead className="text-right">Additions</TableHead><TableHead className="text-right">Utilisation</TableHead>
                    <TableHead className="text-right">Closing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.fundId}>
                      <TableCell><button type="button" className="text-left font-semibold text-[#111827] hover:underline" onClick={() => openLedger(r.ledgerId)}>{r.fundName}</button></TableCell>
                      <TableCell className="text-right">{formatCurrency(r.openingBalance)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.additions)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.utilisation)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(r.closingBalance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 border-[#111827] font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.totals.openingBalance)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.totals.additions)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.totals.utilisation)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.totals.closingBalance)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </SectionCard>
        )}
      </div>

      {data && rows.length > 0 && (
        <div className="report-print-only">
          <ReportPrintShell reportName="Fund Position" rangeLabel={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
            <table className="w-full border-collapse text-[10px]">
              <thead><tr className="border-b border-black"><th className="py-1 text-left">Fund</th><th className="py-1 text-right">Opening</th><th className="py-1 text-right">Additions</th><th className="py-1 text-right">Utilisation</th><th className="py-1 text-right">Closing</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.fundId} className="border-b border-gray-300">
                    <td className="py-0.5">{r.fundName}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.openingBalance)}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.additions)}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.utilisation)}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.closingBalance)}</td>
                  </tr>
                ))}
                <tr className="border-t border-black font-bold">
                  <td className="py-1">Total</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.openingBalance)}</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.additions)}</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.utilisation)}</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.closingBalance)}</td>
                </tr>
              </tbody>
            </table>
          </ReportPrintShell>
        </div>
      )}
    </div>
  );
}
